import React, { useState, useEffect } from 'react'
import { usePlayerStore } from '../store/player-store'
import { useUIStore } from '../store/ui-store'
import { useGameStore } from '../store/game-store'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface TableInfo {
  id: string
  name: string
  tableSize: number
  stakeLevel: string
  isPrivate: boolean
  ownerId: string
  playerCount: number
}

export default function TablesScreen(): React.ReactElement {
  const { token, profile } = usePlayerStore()
  const { navigateTo, showToast } = useUIStore()
  const { reset } = useGameStore()
  const [tables, setTables] = useState<TableInfo[]>([])
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [tableSize, setTableSize] = useState<2 | 6 | 9>(6)
  const [stakeLevel, setStakeLevel] = useState('micro')
  const [isPrivate, setIsPrivate] = useState(false)
  const [password, setPassword] = useState('')
  const [joinPassword, setJoinPassword] = useState('')
  const [joiningId, setJoiningId] = useState<string | null>(null)

  const fetchTables = async (): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE}/tables`)
      const json = await res.json()
      if (json.success) setTables(json.data)
    } catch {}
  }

  useEffect(() => { fetchTables() }, [])

  const handleCreate = async (): Promise<void> => {
    if (!name.trim()) return
    try {
      const res = await fetch(`${API_BASE}/tables/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), tableSize, stakeLevel, isPrivate, password: isPrivate ? password : undefined }),
      })
      const json = await res.json()
      if (json.success) {
        showToast('Table created!', 'success')
        setShowCreate(false)
        setName('')
        setPassword('')
        const d = json.data
        reset()
        useGameStore.getState().connectToTable(d.tableId, token, profile?.name || 'Player', token, d.tableSize, d.stakeLevel)
        navigateTo('table')
      } else {
        showToast(json.error || 'Failed to create', 'error')
      }
    } catch {
      showToast('Failed to create table', 'error')
    }
  }

  const handleJoin = async (table: TableInfo): Promise<void> => {
    if (joiningId) return
    setJoiningId(table.id)
    try {
      const body: any = {}
      if (table.isPrivate) {
        if (!joinPassword) {
          showToast('Enter password for private table', 'info')
          setJoiningId(null)
          return
        }
        body.password = joinPassword
      }
      const res = await fetch(`${API_BASE}/tables/${table.id}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (json.success) {
        setJoinPassword('')
        const d = json.data
        reset()
        useGameStore.getState().connectToTable(d.tableId, token, profile?.name || 'Player', token, d.tableSize, d.stakeLevel)
        navigateTo('table')
      } else {
        showToast(json.error || 'Failed to join', 'error')
      }
    } catch {
      showToast('Failed to join table', 'error')
    }
    setJoiningId(null)
  }

  const stakeLabels: Record<string, string> = { micro: '1/2', low: '5/10', mid: '25/50', high: '100/200' }

  return (
    <div className="lobby">
      <div className="lobby-header">
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={() => navigateTo('lobby')}>
          ← Back
        </button>
        <div className="lobby-title">Tables</div>
        <button className="btn btn-gold" style={{ padding: '6px 14px', fontSize: 12 }} onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : 'Create'}
        </button>
      </div>

      {showCreate && (
        <div style={{ marginTop: 16, padding: 16, background: 'rgba(255,255,255,0.05)', borderRadius: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Create Table</div>
          <input placeholder="Table name" maxLength={30} value={name} onChange={e => setName(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 14, marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select value={tableSize} onChange={e => setTableSize(Number(e.target.value) as any)}
              style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 13 }}>
              <option value={2}>Heads Up (2)</option><option value={6}>6-max</option><option value={9}>9-max</option>
            </select>
            <select value={stakeLevel} onChange={e => setStakeLevel(e.target.value)}
              style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 13 }}>
              <option value="micro">Micro (1/2)</option><option value="low">Low (5/10)</option><option value="mid">Mid (25/50)</option><option value="high">High (100/200)</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#aaa', marginBottom: 8 }}>
            <input type="checkbox" checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)} />
            Private table (password required)
          </label>
          {isPrivate && (
            <input type="text" placeholder="Password (min 4 chars)" value={password} onChange={e => setPassword(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 14, marginBottom: 8 }} />
          )}
          <button className="btn btn-gold" style={{ width: '100%' }} onClick={handleCreate}>Create & Join</button>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {tables.length === 0 ? (
          <div className="flex-center" style={{ padding: 40, color: '#666', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 40 }}>♠</div>
            <div>No tables yet</div>
            <button className="btn btn-gold" onClick={() => setShowCreate(true)}>Create the first table</button>
          </div>
        ) : (
          tables.map(t => {
            const isOwner = t.ownerId === token
            return (
              <div key={t.id} style={{
                padding: '12px 16px', background: 'rgba(255,255,255,0.05)', borderRadius: 12, marginBottom: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                      {t.name} {isOwner && <span style={{ fontSize: 11, color: '#f5c518' }}>👑 Dealer</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      {stakeLabels[t.stakeLevel] || t.stakeLevel} · {t.tableSize}-max{t.isPrivate ? ' · 🔒 Private' : ''}
                    </div>
                  </div>
                  <button className="btn btn-gold" style={{ padding: '6px 14px', fontSize: 12 }}
                    disabled={joiningId === t.id}
                    onClick={() => {
                      if (t.isPrivate) {
                        const pw = prompt('Enter password:')
                        if (pw) { setJoinPassword(pw); handleJoin({ ...t, isPrivate: true }) }
                      } else {
                        handleJoin(t)
                      }
                    }}>
                    {joiningId === t.id ? 'Joining...' : 'Join'}
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
